import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChipSelect, Loader } from "@/components/Common";
import { api, errMsg, useCatalog, SEARCH_KEY } from "@/lib/api";

const STEPS = ["Location", "Type of care", "Who needs care", "Services & timing", "Preferences"];

export default function FindCare() {
  const { data: cat } = useCatalog();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [zipInfo, setZipInfo] = useState(null);
  const [a, setA] = useState({ zip: sp.get("zip") || "", county_id: "", care_type_id: "", recipient: "", age_group: "", services: [], schedule: "", urgency: "", payment_preferences: [], languages: [], accessibility_needs: "", transportation_needed: false });
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!/^\d{5}$/.test(a.zip)) { setZipInfo(null); return; }
    api.get(`/catalog/zip/${a.zip}`).then(({ data }) => { setZipInfo(data); if (data.found) set("county_id", data.county_id); });
  }, [a.zip]);

  useEffect(() => {
    const c = cat?.service_categories.find((x) => x.key === sp.get("type"));
    if (c) setA((p) => ({ ...p, care_type_id: c.id }));
  }, [cat, sp]);

  if (!cat) return <Loader />;
  const care = cat.service_categories.find((c) => c.id === a.care_type_id);
  const valid = [/^\d{5}$/.test(a.zip) && a.county_id, !!a.care_type_id, a.recipient && a.age_group, a.schedule && a.urgency, true][step];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/care-search", { ...a, state_code: "PA", county_id: a.county_id || null });
      localStorage.setItem(SEARCH_KEY, data.id);
      nav(`/find-care/results/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="cz-hero-bg min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="care-wizard">
        <span className="cz-eyebrow">Find Care · Step {step + 1} of {STEPS.length}</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#003B2D] mt-4">{["Where is care needed?", "What type of care do you need?", "Who needs care?", "What help, and when?", "Any preferences?"][step]}</h1>
        <p className="text-slate-500 mt-2">No account needed. We only ask for general information — never medical records.</p>
        <Progress value={((step + 1) / STEPS.length) * 100} className="mt-6 h-1.5" />
        <div className="cz-card p-6 sm:p-8 mt-8 space-y-6">
          {step === 0 && (
            <>
              <div className="space-y-1.5"><Label>ZIP code</Label>
                <Input inputMode="numeric" maxLength={5} value={a.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} placeholder="e.g. 19103" className="h-12 text-lg" data-testid="wizard-zip-input" />
                {zipInfo?.found && <p className="text-sm text-teal-700 flex items-center gap-1" data-testid="wizard-zip-county"><MapPin className="h-4 w-4" />{zipInfo.county_name} County, {zipInfo.state_code}</p>}
              </div>
              {zipInfo && !zipInfo.found && (
                <div className="space-y-1.5"><Label>Select your county</Label>
                  <Select value={a.county_id} onValueChange={(v) => set("county_id", v)}>
                    <SelectTrigger className="h-12" data-testid="wizard-county-select"><SelectValue placeholder="Choose county" /></SelectTrigger>
                    <SelectContent>{cat.counties.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} County, {c.state_code}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Zunvel currently serves Pennsylvania. More states coming soon.</p>
                </div>
              )}
            </>
          )}
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="wizard-care-types">
              {cat.service_categories.map((c) => (
                <button key={c.id} type="button" onClick={() => { set("care_type_id", c.id); set("services", []); }} data-testid={`wizard-care-type-${c.key}`}
                  className={`text-left rounded-xl border p-4 transition-colors ${a.care_type_id === c.id ? "border-teal-600 bg-teal-50 ring-2 ring-teal-600/20" : "border-slate-200 hover:border-teal-300 bg-white"}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{c.name}</span>{a.care_type_id === c.id && <Check className="h-4 w-4 text-teal-700" />}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-1">{c.subservices.slice(0, 3).join(" · ")}</div>
                </button>
              ))}
            </div>
          )}
          {step === 2 && (
            <>
              <div className="space-y-2"><Label>Care is for</Label><ChipSelect single options={cat.recipients} value={a.recipient} onChange={(v) => set("recipient", v)} testid="wizard-recipient" /></div>
              <div className="space-y-2"><Label>General age group</Label><ChipSelect single options={cat.age_groups} value={a.age_group} onChange={(v) => set("age_group", v)} testid="wizard-age-group" /></div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="space-y-2"><Label>Services needed (optional)</Label><ChipSelect options={care?.subservices || []} value={a.services} onChange={(v) => set("services", v)} testid="wizard-services" /></div>
              <div className="space-y-2"><Label>Preferred schedule</Label><ChipSelect single options={cat.schedules} value={a.schedule} onChange={(v) => set("schedule", v)} testid="wizard-schedule" /></div>
              <div className="space-y-2"><Label>How soon?</Label><ChipSelect single options={cat.urgency} value={a.urgency} onChange={(v) => set("urgency", v)} testid="wizard-urgency" /></div>
            </>
          )}
          {step === 4 && (
            <>
              <div className="space-y-2"><Label>Insurance or payment preference</Label><ChipSelect options={cat.payment_methods} value={a.payment_preferences} onChange={(v) => set("payment_preferences", v)} testid="wizard-payment" /></div>
              <div className="space-y-2"><Label>Preferred language</Label><ChipSelect options={cat.languages} value={a.languages} onChange={(v) => set("languages", v)} testid="wizard-languages" /></div>
              <div className="space-y-1.5"><Label>Accessibility needs (optional, no medical details)</Label><Input maxLength={300} value={a.accessibility_needs} onChange={(e) => set("accessibility_needs", e.target.value)} placeholder="e.g. wheelchair accessible entrance" data-testid="wizard-accessibility-input" /></div>
              <label className="flex items-center justify-between rounded-xl border border-slate-200 p-4"><span className="text-sm font-medium text-slate-800">Transportation to appointments is needed</span><Switch checked={a.transportation_needed} onCheckedChange={(v) => set("transportation_needed", v)} data-testid="wizard-transport-switch" /></label>
            </>
          )}
          <div className="flex justify-between pt-2">
            <Button variant="ghost" className="rounded-full" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back-btn"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
            {step < STEPS.length - 1 ? (
              <Button className="rounded-full bg-[#003B2D] hover:bg-[#00281F] px-6" disabled={!valid} onClick={() => setStep(step + 1)} data-testid="wizard-next-btn">Continue<ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button className="rounded-full zv-btn-gold px-6" disabled={busy} onClick={submit} data-testid="search-care-submit-btn">{busy ? "Matching…" : "Find my matches"}<ArrowRight className="h-4 w-4 ml-1" /></Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
